import { Injectable } from '@angular/core';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { LocalStorageKeys } from 'src/app/enum/local-storage-keys.enum';

/**
 * Servicio específico para guardar/obtener tokens
 * Garantiza que siempre uses las claves correctas y tipos adecuados
 * 
 * ⚠️ IMPORTANTE - Expiración de Tokens:
 * - Azure AD gestiona la expiración REAL del JWT (típicamente 1 hora)
 * - Este servicio guarda una "business rule" de 48h en localStorage
 * - La expiración de 48h NO SUSTITUYE la expiración real del JWT
 * - Es una política de la aplicación para forzar re-login periódico
 * - MSAL valida automáticamente la expiración real del JWT
 */
@Injectable({
  providedIn: 'root',
})
export class TypedTokenStorageService {
  constructor(private localStorageService: LocalStorageService) {}

  // ============ ACCESS TOKEN ============
  setAccessToken(token: string): void {
    const cleaned = this.cleanToken(token);
    this.localStorageService.set(LocalStorageKeys.ACCESSTOKEN, cleaned);
  }

  getAccessToken(): string | null {
    const token = this.localStorageService.get(
      LocalStorageKeys.ACCESSTOKEN
    ) as string | null;
    return token ? this.cleanToken(token) : null;
  }

  // ============ ID TOKEN ============
  setIdToken(token: string): void {
    const cleaned = this.cleanToken(token);
    this.localStorageService.set(LocalStorageKeys.IDTOKEN, cleaned);
  }

  getIdToken(): string | null {
    const token = this.localStorageService.get(
      LocalStorageKeys.IDTOKEN
    ) as string | null;
    return token ? this.cleanToken(token) : null;
  }

  // ============ TIMESTAMP ============
  setTokenTimestamp(timestamp: number): void {
    this.localStorageService.set(
      LocalStorageKeys.TOKENTIMESTAMP,
      timestamp.toString()
    );
  }

  getTokenTimestamp(): number | null {
    const timestamp = this.localStorageService.get(
      LocalStorageKeys.TOKENTIMESTAMP
    ) as string | null;
    return timestamp ? Number(timestamp) : null;
  }

  // ============ EXPIRATION (BUSINESS RULE) ============
  setTokenExpiration(expirationDate: Date): void {
    this.localStorageService.set(
      LocalStorageKeys.TOKENEXPIRATION,
      expirationDate.toISOString()
    );
  }

  getTokenExpiration(): Date | null {
    const expiration = this.localStorageService.get(
      LocalStorageKeys.TOKENEXPIRATION
    ) as string | null;
    return expiration ? new Date(expiration) : null;
  }

  // ============ SAVE ALL AT ONCE ============
  saveAllTokens(
    accessToken: string,
    idToken: string,
    expirationHours: number = 48
  ): void {
    if (!accessToken || !idToken) {
      return;
    }

    this.setAccessToken(accessToken);
    this.setIdToken(idToken);
    this.setTokenTimestamp(Date.now());
    
    // Esta es una business rule, no la expiración real del JWT
    const expirationDate = new Date(Date.now() + expirationHours * 60 * 60 * 1000);
    this.setTokenExpiration(expirationDate);
  }

  // ============ UTILITIES ============
  private cleanToken(token: string): string {
    return token.replace(/^"(.*)"$/, '$1');
  }

  getAllTokens() {
    return {
      accessToken: this.getAccessToken(),
      idToken: this.getIdToken(),
      timestamp: this.getTokenTimestamp(),
      expiration: this.getTokenExpiration(),
    };
  }
}
